import { useEffect } from 'react';

/**
 * Hook that triggers a refetch function when the page becomes visible/focused
 * Useful for keeping data fresh when users return to the tab
 *
 * @param refetch - Function to call when page becomes visible (e.g., Apollo's refetch function)
 *
 * @example
 * ```tsx
 * const { data, refetch } = useQuery(SomeDocument);
 * useRefetchOnPageFocus(refetch);
 * ```
 */
export const useRefetchOnPageFocus = (refetch: () => void | Promise<unknown>) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);
};
